'use client';

import React from 'react';
import { alpha, Box, Card, CardContent, Typography } from '@mui/material';

export interface DepositOption {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  logos?: React.ReactNode;
  onClick: () => void;
}

interface DepositOptionItemProps {
  option: DepositOption;
}

export const DepositOptionItem = ({ option }: DepositOptionItemProps) => {
  return (
    <Card
      onClick={option.onClick}
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.subtle',
        boxShadow: (theme) =>
          `0 2px 8px ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.08)}`,
        borderRadius: 2.5,
        '&:hover': {
          bgcolor: 'background.hover',
          boxShadow: (theme) =>
            `0 4px 12px ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.25 : 0.12)}`,
          borderColor: 'primary.main',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          '&:last-child': { pb: 2 },
          flexDirection: 'row',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              flexShrink: 0,
              borderRadius: '6px',
              bgcolor: 'info.bg',
              color: 'primary.contrastText',
            }}
          >
            {option.icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" fontWeight={700} sx={{ mb: 0.25, fontSize: '0.9375rem' }}>
              {option.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
              {option.subtitle}
            </Typography>
          </Box>
        </Box>
        {option.logos && (
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>{option.logos}</Box>
        )}
      </CardContent>
    </Card>
  );
};
